import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle2, Star, XCircle,
} from 'lucide-react';
import { LoadingSkeleton } from './_shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContentReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('admin_token');
      const resp = await fetch(`${API_URL}/api/admin/content-reviews`, { headers: { Authorization: `Bearer ${token}` } });
      if (resp.ok) setReviews(await resp.json());
    } catch (e) { console.error('Failed to load content reviews:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const handleAction = async (reviewId, action) => {
    const token = sessionStorage.getItem('admin_token');
    await fetch(`${API_URL}/api/admin/content-reviews/${reviewId}?action=${action}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchReviews();
  };

  const filtered = filter === 'all' ? reviews : reviews.filter(r => r.status === filter);
  const counts = { all: reviews.length, pending: reviews.filter(r => r.status === 'pending').length, approved: reviews.filter(r => r.status === 'approved').length, rejected: reviews.filter(r => r.status === 'rejected').length };

  if (loading) return <LoadingSkeleton />;

  return (
    <div className="space-y-6" data-testid="admin-reviews-tab">
      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'pending', 'approved', 'rejected'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${filter === f ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            data-testid={`filter-${f}`}>
            {f.charAt(0).toUpperCase() + f.slice(1)} ({counts[f]})
          </button>
        ))}
      </div>

      {/* Reviews list */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400">No {filter} reviews found</div>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => (
            <div key={r.review_id} className={`bg-white rounded-xl border p-5 ${r.is_featured ? 'border-amber-300 ring-1 ring-amber-200' : 'border-gray-100'}`} data-testid={`admin-review-${r.review_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="font-semibold text-sm text-gray-900">{r.name}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                      r.status === 'approved' ? 'bg-green-50 text-green-600' :
                      r.status === 'pending' ? 'bg-amber-50 text-amber-600' :
                      'bg-red-50 text-red-600'
                    }`}>{r.status}</span>
                    {r.is_featured && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Featured</span>}
                    {r.service_type && <span className="text-[10px] text-gray-400">{r.service_type}</span>}
                  </div>
                  <p className="text-xs text-gray-500 mb-2">{r.role}{r.location ? ` \u2022 ${r.location}` : ''}</p>
                  <div className="flex gap-0.5 mb-2">
                    {Array.from({ length: r.rating || 5 }, (_, k) => `s-${r.review_id || r.id || k}-${k}`).map(k => <Star key={k} className="w-3 h-3 text-amber-400 fill-amber-400" />)}
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">"{r.quote}"</p>
                  <p className="text-[10px] text-gray-400 mt-2">{r.created_at ? new Date(r.created_at).toLocaleDateString() : 'N/A'}</p>
                </div>
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  {r.status !== 'approved' && (
                    <button onClick={() => handleAction(r.review_id, 'approve')}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors" data-testid={`approve-${r.review_id}`}>
                      <CheckCircle2 className="w-3 h-3" /> Approve
                    </button>
                  )}
                  {r.status !== 'rejected' && (
                    <button onClick={() => handleAction(r.review_id, 'reject')}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors" data-testid={`reject-${r.review_id}`}>
                      <XCircle className="w-3 h-3" /> Reject
                    </button>
                  )}
                  {r.status === 'approved' && !r.is_featured && (
                    <button onClick={() => handleAction(r.review_id, 'feature')}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] bg-amber-50 text-amber-600 rounded-lg hover:bg-amber-100 transition-colors" data-testid={`feature-${r.review_id}`}>
                      <Star className="w-3 h-3" /> Feature
                    </button>
                  )}
                  {r.is_featured && (
                    <button onClick={() => handleAction(r.review_id, 'unfeature')}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] bg-gray-100 text-gray-500 rounded-lg hover:bg-gray-200 transition-colors" data-testid={`unfeature-${r.review_id}`}>
                      <Star className="w-3 h-3" /> Unfeature
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
